import {
  trackRemoteIntroPromoDismissed,
  trackRemoteIntroPromoViewed,
} from '@/models/analytics/features/trackRemoteIntroPromoAnalytics';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Dialog, DialogContent } from '@heidi/design-system/dialog';
import { type RemoteIntroPromoCohort } from '@heidi/growth/contracts';
import { useAtom, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';

import {
  remoteIntroPromoDevForceCohortAtom,
  remoteIntroPromoModalEntrySurfaceAtom,
  remoteIntroPromoModalOpenAtom,
} from '../atoms/remote-intro-promo-atoms';
import { useRemoteIntroPromoEligibility } from '../eligibility/useIsRemoteIntroPromoEligible';

const RemoteIntroPromoModalContent = dynamic(
  () =>
    import('./remote-intro-promo-modal-content').then(
      (m) => m.RemoteIntroPromoModalContent
    ),
  { ssr: false }
);

const SURFACE = 'modal' as const;

/**
 * Shared promo Dialog opened by the Billing-page banner CTA and the
 * profile-menu card. Always mounted; only renders the heavy content lazily
 * when `remoteIntroPromoModalOpenAtom` flips to `true`. Impression and
 * dismiss analytics fire on every open→close transition regardless of how
 * the user closes — the atom is the single source of truth, so an effect
 * watching its value catches both programmatic opens (banner / menu) and
 * Radix's onOpenChange (X / ESC / outside click).
 */
export const RemoteIntroPromoModal = () => {
  const [open, setOpen] = useAtom(remoteIntroPromoModalOpenAtom);
  const setEntrySurface = useSetAtom(remoteIntroPromoModalEntrySurfaceAtom);
  const setDevForceCohort = useSetAtom(remoteIntroPromoDevForceCohortAtom);
  const cohort = useRemoteIntroPromoEligibility() ?? 'monthly_subscriber';
  const wasOpenRef = useRef(false);
  // Snapshot the cohort at open-time so a later `dismissed` event is
  // attributed to the same cohort the user *saw* the modal under, even if
  // eligibility changes while it stays open.
  const openedCohortRef = useRef<RemoteIntroPromoCohort | null>(null);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      openedCohortRef.current = cohort;
      trackRemoteIntroPromoViewed(SURFACE, cohort);
    } else if (!open && wasOpenRef.current) {
      trackRemoteIntroPromoDismissed(SURFACE, openedCohortRef.current ?? cohort);
      openedCohortRef.current = null;
      // Reset so a future open from a different surface doesn't inherit the
      // previous entry surface as stale UTM / event metadata.
      setEntrySurface(null);
    }
    wasOpenRef.current = open;
  }, [open, cohort, setEntrySurface]);

  // Dev/staging-only console helpers. Lets QA preview the modal + surfaces
  // without satisfying every eligibility gate. Usage in DevTools:
  //   __remoteIntroPromo.show('monthly')       // force eligibility + open modal
  //   __remoteIntroPromo.show('yearly')
  //   __remoteIntroPromo.show('free')
  //   __remoteIntroPromo.forceShow('monthly')  // force surfaces only (banner + menu card)
  //   __remoteIntroPromo.unforceShow()         // back to real eligibility
  //   __remoteIntroPromo.open()                // open modal w/o forcing cohort
  //   __remoteIntroPromo.close()               // close modal + clear force
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (getEnvironment() === ENV_NAME.PROD) return;

    const COHORT_ALIASES: Record<string, RemoteIntroPromoCohort> = {
      monthly: 'monthly_subscriber',
      monthly_subscriber: 'monthly_subscriber',
      yearly: 'yearly_subscriber',
      yearly_subscriber: 'yearly_subscriber',
      free: 'free_user',
      free_user: 'free_user',
    };
    const resolveCohort = (c: string): RemoteIntroPromoCohort | null =>
      COHORT_ALIASES[c] ?? null;

    const w = window as unknown as Record<string, unknown>;
    w.__remoteIntroPromo = {
      open: () => setOpen(true),
      close: () => {
        setOpen(false);
        setDevForceCohort(null);
      },
      forceShow: (cohort: string) => {
        const resolved = resolveCohort(cohort);
        if (!resolved) {
          // eslint-disable-next-line no-console
          console.warn(
            `[remoteIntroPromo] unknown cohort '${cohort}' — use monthly | yearly | free`
          );
          return;
        }
        setDevForceCohort(resolved);
      },
      unforceShow: () => setDevForceCohort(null),
      show: (cohort: string) => {
        const resolved = resolveCohort(cohort);
        if (!resolved) {
          // eslint-disable-next-line no-console
          console.warn(
            `[remoteIntroPromo] unknown cohort '${cohort}' — use monthly | yearly | free`
          );
          return;
        }
        setDevForceCohort(resolved);
        setOpen(true);
      },
    };
    return () => {
      delete w.__remoteIntroPromo;
    };
  }, [setOpen, setDevForceCohort]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[940px] gap-0 overflow-hidden rounded-2xl border border-border bg-surface p-0 shadow-2xl"
        // At lg+, the built-in close X sits over the fixed-light yellow hero
        // panel (`bg-sunlight-100`). Pin it to a token that's always dark so
        // it stays readable in dark mode — `text-text-primary` would flip to
        // white and disappear on the yellow. Below lg the hero panel is
        // hidden and the X overlays the theme-aware `bg-surface`, so we
        // leave the default `text-text-primary` flip alone there.
        closeIconClassName="lg:text-text-primary-on-light"
        data-testid="remote-intro-promo-modal"
      >
        {open && <RemoteIntroPromoModalContent />}
      </DialogContent>
    </Dialog>
  );
};
